import { useCallback, useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { getEntryTraffic } from "../utils/api";
import { useDashboardWebSocket } from "../contexts/DashboardWebSocketContext";
import { useDashboardLive } from "../contexts/DashboardLiveContext";

const ENTRY_TRAFFIC_WS_FALLBACK_MS = 8000;

const TRAFFIC_MESSAGE_TYPES = new Set([
  "entry_traffic_snapshot",
  "entry_traffic_update",
  "entry_traffic_error",
]);

function isTodayDateString(dateStr) {
  if (!dateStr) return false;
  return dateStr === format(new Date(), "yyyy-MM-dd");
}

function applyTrafficMetrics(payload) {
  const timeSeries = payload?.time_series ?? [];
  const peakFromApi = payload?.peak_occupancy;
  const currentFromApi = payload?.current_present;

  const peakValue = typeof peakFromApi === "number"
    ? peakFromApi
    : (timeSeries.length > 0 ? Math.max(...timeSeries.map((item) => item.count || 0)) : 0);

  const currentValue = typeof currentFromApi === "number"
    ? currentFromApi
    : (timeSeries.length > 0 ? timeSeries[timeSeries.length - 1].count || 0 : 0);

  return {
    timeSeries,
    peakOccupancy: peakValue,
    currentPresent: currentValue,
  };
}

export function useEntryTrafficSocket({ date }) {
  const { subscribe, send, getReadyState } = useDashboardWebSocket();
  const { isLiveEnabled, liveVersion } = useDashboardLive();

  const [timeSeries, setTimeSeries] = useState([]);
  const [peakOccupancy, setPeakOccupancy] = useState(0);
  const [currentPresent, setCurrentPresent] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const hasDataRef = useRef(false);
  const usedRestFallbackRef = useRef(false);
  const activeDateRef = useRef(date);
  const isLiveEnabledRef = useRef(isLiveEnabled);
  const useWebsocketRef = useRef(isLiveEnabled && isTodayDateString(date));
  const pendingDateRef = useRef(null);
  const fallbackTimerRef = useRef(null);
  const trafficSubscribedRef = useRef(false);

  activeDateRef.current = date;
  isLiveEnabledRef.current = isLiveEnabled;
  useWebsocketRef.current = isLiveEnabled && isTodayDateString(date);

  const clearFallbackTimer = useCallback(() => {
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
  }, []);

  const applyEntryTrafficPayload = useCallback((payload) => {
    if (payload?.type === "entry_traffic_error") {
      setError(payload.detail || "Unable to load employee presence data");
      setLoading(false);
      return;
    }

    if (
      payload?.type === "entry_traffic_snapshot" ||
      payload?.type === "entry_traffic_update"
    ) {
      if (payload?.date && payload.date !== activeDateRef.current) {
        return;
      }

      const metrics = applyTrafficMetrics(payload);
      hasDataRef.current = true;
      clearFallbackTimer();
      setTimeSeries(metrics.timeSeries);
      setPeakOccupancy(metrics.peakOccupancy);
      setCurrentPresent(metrics.currentPresent);
      setError(null);
      setLoading(false);
      return;
    }

    if (payload?.type === "dashboard_ws_error" && payload.code === 4401) {
      setError("Session expired. Please sign in again.");
      setLoading(false);
    }
  }, [clearFallbackTimer]);

  const fetchEntryTrafficRestFallback = useCallback(async (requestedDate) => {
    const targetDate = requestedDate || activeDateRef.current;
    if (usedRestFallbackRef.current || hasDataRef.current) {
      return;
    }

    const readyState = getReadyState();
    if (readyState === WebSocket.CONNECTING || readyState === WebSocket.OPEN) {
      return;
    }

    usedRestFallbackRef.current = true;
    clearFallbackTimer();
    try {
      const result = await getEntryTraffic(targetDate);
      applyEntryTrafficPayload({
        type: "entry_traffic_snapshot",
        ...result,
      });
    } catch (err) {
      console.error("[Entry Traffic WS] REST fallback failed:", err);
      setError(err.message || "Unable to load employee presence data");
      setLoading(false);
    }
  }, [applyEntryTrafficPayload, clearFallbackTimer, getReadyState]);

  const fetchEntryTrafficRest = useCallback(async (requestedDate, options = {}) => {
    const { showLoading = true } = options;
    const targetDate = requestedDate || activeDateRef.current;

    if (showLoading) {
      setLoading(true);
    }
    setError(null);
    hasDataRef.current = false;
    usedRestFallbackRef.current = false;

    try {
      const result = await getEntryTraffic(targetDate);
      applyEntryTrafficPayload({
        type: "entry_traffic_snapshot",
        ...result,
      });
    } catch (err) {
      console.error("[Entry Traffic] REST fetch failed:", err);
      setError(err.message || "Unable to load employee presence data");
      setTimeSeries([]);
      setPeakOccupancy(0);
      setCurrentPresent(0);
      setLoading(false);
    }
  }, [applyEntryTrafficPayload]);

  const scheduleRestFallback = useCallback((requestedDate) => {
    clearFallbackTimer();
    fallbackTimerRef.current = setTimeout(() => {
      fallbackTimerRef.current = null;
      if (hasDataRef.current || usedRestFallbackRef.current) {
        return;
      }
      if (getReadyState() === WebSocket.OPEN) {
        return;
      }
      fetchEntryTrafficRestFallback(requestedDate || activeDateRef.current);
    }, ENTRY_TRAFFIC_WS_FALLBACK_MS);
  }, [clearFallbackTimer, fetchEntryTrafficRestFallback, getReadyState]);

  const sendTrafficDateOrQueue = useCallback((targetDate) => {
    const message = {
      type: "set_traffic_date",
      date: targetDate,
      tz_offset_minutes: new Date().getTimezoneOffset(),
    };

    if (send(message)) {
      pendingDateRef.current = null;
      return true;
    }

    if (getReadyState() === WebSocket.CONNECTING) {
      pendingDateRef.current = targetDate;
      return true;
    }

    return false;
  }, [getReadyState, send]);

  const subscribeTrafficOnSocket = useCallback(() => {
    if (trafficSubscribedRef.current) {
      return;
    }
    const registered = send({
      type: "register_entry_traffic",
      date: activeDateRef.current,
      tz_offset_minutes: new Date().getTimezoneOffset(),
    });
    trafficSubscribedRef.current = registered;
  }, [send]);

  const unsubscribeTrafficOnSocket = useCallback(() => {
    if (!trafficSubscribedRef.current) {
      return;
    }
    send({ type: "unregister_entry_traffic" });
    trafficSubscribedRef.current = false;
  }, [send]);

  const refresh = useCallback((dateOverride, options = {}) => {
    const { showLoading = true } = options;
    const targetDate = dateOverride || activeDateRef.current;
    activeDateRef.current = targetDate;
    useWebsocketRef.current = isLiveEnabledRef.current && isTodayDateString(targetDate);

    if (showLoading) {
      setLoading(true);
    }
    setError(null);
    hasDataRef.current = false;
    usedRestFallbackRef.current = false;

    if (useWebsocketRef.current) {
      subscribeTrafficOnSocket();

      if (sendTrafficDateOrQueue(targetDate)) {
        scheduleRestFallback(targetDate);
        return;
      }

      scheduleRestFallback(targetDate);
      return;
    }

    unsubscribeTrafficOnSocket();
    fetchEntryTrafficRest(targetDate, { showLoading });
  }, [
    fetchEntryTrafficRest,
    scheduleRestFallback,
    sendTrafficDateOrQueue,
    subscribeTrafficOnSocket,
    unsubscribeTrafficOnSocket,
  ]);

  useEffect(() => {
    activeDateRef.current = date;
    useWebsocketRef.current = isLiveEnabled && isTodayDateString(date);
  }, [date, isLiveEnabled]);

  useEffect(() => {
    hasDataRef.current = false;
    usedRestFallbackRef.current = false;
    activeDateRef.current = date;
    useWebsocketRef.current = isLiveEnabled && isTodayDateString(date);
    trafficSubscribedRef.current = false;
    setLoading(true);
    setError(null);

    const unsubscribe = subscribe((payload) => {
      if (payload?.type === "__dashboard_ws_open") {
        if (useWebsocketRef.current) {
          subscribeTrafficOnSocket();
          send({
            type: "set_traffic_date",
            date: pendingDateRef.current || activeDateRef.current,
            tz_offset_minutes: new Date().getTimezoneOffset(),
          });
          pendingDateRef.current = null;
        }
        return;
      }
      if (!TRAFFIC_MESSAGE_TYPES.has(payload?.type) && payload?.type !== "dashboard_ws_error") {
        return;
      }
      applyEntryTrafficPayload(payload);
    });

    if (useWebsocketRef.current) {
      subscribeTrafficOnSocket();
      scheduleRestFallback(date);
    } else {
      unsubscribeTrafficOnSocket();
      fetchEntryTrafficRest(date, { showLoading: true });
    }

    return () => {
      clearFallbackTimer();
      unsubscribe();
      unsubscribeTrafficOnSocket();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLiveEnabled, liveVersion]);

  return {
    timeSeries,
    peakOccupancy,
    currentPresent,
    loading,
    error,
    refresh,
  };
}
